import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  ProtectedResourceScopes,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';

import { routes } from './app.routes';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
// import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// import { HttpRequestInterceptor } from './services/spinner-interceptor';
import { provideRouter } from '@angular/router';
import { HttpErrorInterceptor } from './interceptors/http-error.service';

import { APP_INITIALIZER } from '@angular/core';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideNativeDateAdapter } from '@angular/material/core';
import { LOCALE_ID } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';


export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel >= LogLevel.Warning) {
    console.warn('[MSAL]', message);
  }
}

export function initializeMsal(msalInstance: PublicClientApplication): () => Promise<void> {
  return () => {
    return msalInstance.initialize(); //
  };
}

export const b2cPolicies = {
  names: {
    signUpSignIn: 'b2c_1_susi',
    resetPassword: 'b2c_1_password_reset',
    editProfile: 'b2c_1_profile_edit',
  },
  authorities: {
    signUpSignIn: { authority: 'https://mataprod.b2clogin.com/mataprod.onmicrosoft.com/b2c_1_susi' },
    resetPassword: { authority: 'https://mataprod.b2clogin.com/mataprod.onmicrosoft.com/b2c_1_susi' },
    editProfile: { authority: 'https://mataprod.b2clogin.com/mataprod.onmicrosoft.com/b2c_1_susi' },
  },
  authorityDomain: 'mataprod.b2clogin.com',
};


export function MSALInstanceFactory(): IPublicClientApplication {
  // Hole Authority/Domain/Redirect aus dem environment; fallback auf origin
  const authority = (environment as any).adb2cConfig.authority
    ?? (environment as any).adb2cConfig.readScopeUrl; // Fallback, falls authority noch nicht eingeführt

  // Domain aus Authority ableiten, falls notiert
  const authorityDomain = (environment as any).adb2cConfig.knownAuthorities?.[0]
    ?? (new URL(authority)).host;

  const redirectUri = (environment as any).adb2cConfig.redirectUri ?? window.location.origin;
  const postLogoutRedirectUri = (environment as any).adb2cConfig.postLogoutRedirectUri ?? window.location.origin;

  return new PublicClientApplication({
    auth: {
      clientId: environment.adb2cConfig.clientId,
      authority,                                   // ← B2C-Authority (Prod)
      knownAuthorities: [authorityDomain],         // ← WICHTIG bei B2C (verhindert Instance-Discovery-400)
      redirectUri,
      postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();
  const allHttpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;

  // Nutze für die API-Calls ausschließlich die Client-ID als Scope
  const scopes = [...environment.adb2cConfig.scopeUrls]; // ['46396a08-...']

  const apiBase = environment.adb2cConfig.apiEndpointUrl ?? environment.apiBaseUrl;

  protectedResourceMap.set(
    `${apiBase}/*`,
    allHttpMethods.map(method => ({ httpMethod: method, scopes }))
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}


export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      // Für den Login OIDC + deine "Legacy-API-Scope" (Client-ID)
      scopes: ['openid', ...environment.adb2cConfig.scopeUrls],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        timeOut: 3000,
        positionClass: 'toast-top-right',
        preventDuplicates: true,
      }),
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    provideCharts(withDefaultRegisterables()),
    provideNativeDateAdapter(),
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    provideAnimations(),
  ],
};

